import humps from 'humps';
import { put, retry, all, select } from 'redux-saga/effects';
import { reject, resolve } from '../api';
import * as types from '../constants';
import { authClient } from '../api/Client';
import {
  showNotification,
  simpleErrorNotification,
  successNotification,
} from '../../shared-components/Notifications';
import { getAllAmberReports, selectFeeds, selectTags } from '../selectors/blog';

export function* loadAmberReports({ currentPage }) {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.loadAmberReports, {
      currentPage,
    });

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.LOAD_AMBER_REPORTS),
        payload: humps?.camelizeKeys(res?.data()),
      });
    } else {
      yield all([
        put({
          type: reject(types?.LOAD_AMBER_REPORTS),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading Amber Reports was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* loadAmberReport({ uuid }) {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.loadAmberReport, {
      uuid,
    });

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.LOAD_AMBER_REPORT),
        payload: humps?.camelizeKeys(res?.data()),
      });
    } else {
      yield all([
        put({
          type: reject(types?.LOAD_AMBER_REPORT),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading this report was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* loadAmberReportCount() {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.loadAmberReportCount);

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.LOAD_AMBER_REPORT_COUNT),
        payload: humps?.camelizeKeys(res?.data()),
      });
    } else {
      yield all([
        put({
          type: reject(types?.LOAD_AMBER_REPORT_COUNT),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading Amber Report Count was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* loadAmberReportFeeds() {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.loadAmberReportFeeds);

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.LOAD_AMBER_REPORT_FEEDS),
        payload: selectFeeds(humps?.camelizeKeys(res?.data())),
      });
    } else {
      yield all([
        put({
          type: reject(types?.LOAD_AMBER_REPORT_FEEDS),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading Amber Report Feeds was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* loadAmberReportTags() {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.loadAmberReportTags);

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.LOAD_AMBER_REPORT_TAGS),
        payload: selectTags(humps?.camelizeKeys(res?.data())),
      });
    } else {
      yield all([
        put({
          type: reject(types?.LOAD_AMBER_REPORT_TAGS),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading Amber Report Tags was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* filterAmberReports({ filterParams }) {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.filterAmberReports, {
      filterParams,
    });

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.FILTER_AMBER_REPORTS),
        payload: humps?.camelizeKeys(res?.data()),
      });
    } else {
      yield all([
        put({
          type: reject(types?.FILTER_AMBER_REPORTS),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading Amber Reports was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* setAmberReportsAsSeen() {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.setAmberReportsAsSeen, {
      body: {
        mark_all: true,
        seen: true,
      },
    });

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.SET_AMBER_REPORTS_AS_SEEN),
        payload: yield select(getAllAmberReports),
      });

      showNotification(
        successNotification({
          statusCode: 200,
          message: 'All reports are set to read!',
        }),
      );
    } else {
      yield all([
        put({
          type: reject(types?.SET_AMBER_REPORTS_AS_SEEN),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'An error has occurred while trying to set all report to read.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* setAmberReportUnseen({ uuid }) {
  try {
    const res = yield retry(4, 500, authClient?.Blog?.setAmberReportUnseen, {
      body: {
        blogs_uuids: uuid,
        seen: false,
      },
    });

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.SET_AMBER_REPORT_UNSEEN),
        payload: yield select(getAllAmberReports),
        uuid: uuid,
      });

      showNotification(
        successNotification({
          statusCode: 200,
          message: 'The report has been set to unread!',
        }),
      );
    } else {
      yield all([
        put({
          type: reject(types?.SET_AMBER_REPORT_UNSEEN),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'An error has occurred while trying to set a report to unread.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}

export function* checkEnrichmentFeedHasBlogs({ uuids }) {
  try {
    const res = yield retry(
      4,
      500,
      authClient?.Blog?.checkEnrichmentFeedHasBlogs,
      {
        uuids,
      },
    );

    if (
      res?.responseStatus === 200 ||
      res?.responseStatus === 201 ||
      res?.responseStatus === 204
    ) {
      yield put({
        type: resolve(types?.CHECK_ENRICHMENT_FEED_HAS_BLOGS),
        payload: humps?.camelizeKeys(res?.data()),
      });
    } else {
      yield all([
        put({
          type: reject(types?.CHECK_ENRICHMENT_FEED_HAS_BLOGS),
          res,
        }),
        showNotification(
          simpleErrorNotification({
            statusCode: res?.responseStatus,
            message:
              JSON.parse(res?.responseData)?.errors?.[0]?.message ??
              'Loading connecting TLP Amber Reports was unsuccessful.',
          }),
        ),
      ]);
    }
  } catch (error) {}
}
